import { t } from '@lingui/core/macro';
import { NoddiBasicCard, NoddiDialog, NoddiSkeleton } from 'noddi-ui-web';
import { compareLicensePlates, formatCurrencyAmount, isLockedBookingSalesItem } from 'noddi-util';
import {
  commonTrackingEvents,
  SalesItemTagBadge,
  tracking,
  useBookingActions,
  useBookingSelectedSalesItemsCars
} from 'noddi-web';
import { useState } from 'react';

import { LicensePlate, SalesItemBookingCategoryType, SalesItemForBookingRecord } from '@navio/nsdk';
import { SALES_ITEM_BOOKING_FLOW_TYPE } from 'noddi-types';
import { cn } from 'noddi-ui-common';
import { useGetUnavailableSalesItems } from '../../../../hooks/useGetUnavailableSalesItems';
import { SalesItemButtons } from './SalesItemButtons';
import { TemporarilyUnavailableModal } from './TemporarilyUnavailableModal';
import { UnavailableModal } from './UnavailableModal';

type BookingPickerSalesItem = SalesItemForBookingRecord & {
  isCustomerEditable?: boolean;
};

type SalesItemProps = {
  salesItem: BookingPickerSalesItem;
  licensePlate: LicensePlate;
  className?: string;
  type: SALES_ITEM_BOOKING_FLOW_TYPE;
};

export const SalesItem = ({ salesItem, licensePlate, className, type }: SalesItemProps) => {
  const [showUnavailableModal, setShowUnavailableModal] = useState(false);
  const [showTemporarilyUnavailableModal, setShowTemporarilyUnavailableModal] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);

  const selectedSalesItemsCars = useBookingSelectedSalesItemsCars();
  const { addSalesItem, removeSalesItem } = useBookingActions();
  const { isUnavailableSalesItemsLoading, unavailableSalesItems } = useGetUnavailableSalesItems();

  const {
    name,
    shortDescription,
    description,
    price,
    salesItemId,
    isAddonsAvailable,
    isWheelPickupRequired,
    isIncomingTireSeasonRequired,
    mutuallyExclusiveSalesItemIds,
    bookingCategoryType,
    temporarilyUnavailable
  } = salesItem;

  const isTemporarilyUnavailable = !!temporarilyUnavailable?.disabled;

  const isAlreadySelected = selectedSalesItemsCars.some(
    (item) => compareLicensePlates(item.licensePlate, licensePlate) && item.salesItemId === salesItemId
  );

  const isUnavailable = (unavailableSalesItems ?? []).some(
    (unavailableSalesItem: { salesItemId: number }) => unavailableSalesItem.salesItemId === salesItemId
  );

  const isFree = price.priceGross.amount === 0;
  const priceFormatted = isFree
    ? t`Free`
    : formatCurrencyAmount({
        money: price.priceGross,
        decimals: 0
      });

  const isLocked = isLockedBookingSalesItem(salesItem);

  const handleSalesItemToggle = () => {
    if (isLocked) {
      return;
    }
    isAlreadySelected
      ? removeSalesItem({
          licensePlate,
          salesItemId,
          type
        })
      : addSalesItem({
          licensePlate,
          salesItemId,
          type,
          mutuallyExclusiveSalesItemIds,
          isAddonsAvailable,
          isWheelPickupRequired,
          isIncomingTireSeasonRequired,
          // todo ts package needs to be enum
          bookingCategoryType: bookingCategoryType as SalesItemBookingCategoryType
        });
  };
  // If sales item is unavailable show modal with explanation
  // Otherwise, add or remove sales item
  const handleSalesItemCardClick = () => {
    if (isTemporarilyUnavailable) {
      setShowTemporarilyUnavailableModal(true);
      return;
    }
    isUnavailable ? setShowUnavailableModal(true) : handleSalesItemToggle();
  };

  const onTemporarilyUnavailableClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setShowTemporarilyUnavailableModal(true);
  };

  const onShowFullDescriptionClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    tracking.track(commonTrackingEvents.salesItemDescriptionDetailsClicked, { salesItemName: name });
    setShowFullDescription(true);
    e.stopPropagation();
  };

  const showSkeletonLoader = isUnavailableSalesItemsLoading && !isAlreadySelected;

  // Remove trailing period from shortDescription if it exists
  const displayShortDescription = shortDescription.endsWith('.') ? shortDescription.slice(0, -1) : shortDescription;

  return (
    <>
      <UnavailableModal
        salesItem={salesItem}
        open={showUnavailableModal}
        onClose={() => setShowUnavailableModal(false)}
        unavailableSalesItems={unavailableSalesItems}
      />
      {!!temporarilyUnavailable && (
        <TemporarilyUnavailableModal
          open={showTemporarilyUnavailableModal}
          onClose={() => setShowTemporarilyUnavailableModal(false)}
          reason={temporarilyUnavailable}
        />
      )}
      <NoddiDialog title={name} onClose={() => setShowFullDescription(false)} open={showFullDescription}>
        {description}
      </NoddiDialog>
      <NoddiBasicCard
        separateAction
        onClick={isUnavailableSalesItemsLoading || isLocked ? undefined : handleSalesItemCardClick}
        selected={isAlreadySelected || isLocked}
        accessibleLabel={t`${name}, ${priceFormatted}`}
        className={cn('flex flex-col gap-2 transition-all duration-200 ease-out', className)}
      >
        <p className='text-lg font-medium'>{name}</p>
        {!!salesItem.tags?.length && (
          <div className='flex flex-wrap gap-1'>
            {[...salesItem.tags]
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((tag) => (
                <SalesItemTagBadge
                  key={tag.id}
                  name={tag.name}
                  colorHex={tag.colorHex}
                  icon={tag.icon}
                  selected={isAlreadySelected}
                />
              ))}
          </div>
        )}
        <p className={cn('font-semibold', isFree && !isAlreadySelected && 'text-successDark')}>{priceFormatted}</p>
        {!!displayShortDescription && <p>{displayShortDescription}</p>}
        <div className='flex h-9 items-end justify-end'>
          {showSkeletonLoader ? (
            <div className='flex gap-2'>
              <NoddiSkeleton variant='button' className='h-9 w-[90px]' />
              <NoddiSkeleton variant='button' className='h-9 w-25' />
            </div>
          ) : (
            <SalesItemButtons
              isUnavailable={isUnavailable}
              isTemporarilyUnavailable={isTemporarilyUnavailable}
              isLocked={isLocked}
              description={description}
              isSelected={isAlreadySelected || isLocked}
              onShowFullDescriptionClick={onShowFullDescriptionClick}
              onTemporarilyUnavailableClick={onTemporarilyUnavailableClick}
            />
          )}
        </div>
      </NoddiBasicCard>
    </>
  );
};
